export const COOKIE_CONSENT_KEY = "nucont:cookie-consent";

/**
 * Sobe quando o texto do aviso ou o que ele controla mudar: quem respondeu a versão antiga vê o
 * aviso de novo.
 */
export const COOKIE_CONSENT_VERSION = 1;

/** Teto do Chrome para a validade de um cookie (400 dias). */
const COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60;

const ACEITO = "accepted";
const RECUSADO = "refused";

/**
 * Consentimento de cookies (LGPD). Os cookies essenciais (sessão, preferência de cookies, chat de
 * atendimento) não dependem da resposta; os de análise (Mixpanel) sim: quem recusa sai do
 * rastreamento.
 *
 * Enquanto a pessoa não responde, a análise segue ligada: o aviso só desliga o que foi recusado.
 *
 * A resposta é pedida uma vez só por navegador. Fica num cookie de `.nucont.com`, que vale para
 * todos os endereços da Nucont (pro-v1, pro-v2, beta, sandbox): quem respondeu num não vê o aviso
 * no outro. O `localStorage` guarda uma cópia para quando o cookie não puder ser gravado.
 *
 * `analytics` é quem liga e desliga as ferramentas de análise (`optIn` / `optOut`); fica injetado
 * para o serviço não depender do Mixpanel.
 */
/** `.nucont.com` em qualquer endereço da Nucont; fora dele (localhost) o cookie fica no host. */
export function cookieDomainFor(hostname = "") {
    return hostname === "nucont.com" || hostname.endsWith(".nucont.com") ? "nucont.com" : null;
}

export class CookieConsent {
    constructor({
        storage = globalThis.localStorage,
        cookies = globalThis.document,
        hostname = globalThis.location?.hostname ?? "",
        analytics = null
    } = {}) {
        this.storage = storage;
        this.cookies = cookies;
        this.hostname = hostname;
        this.analytics = analytics;
    }

    /** `"accepted"`, `"refused"` ou `null` (ainda não respondeu, ou respondeu outra versão). */
    status() {
        const escolha = this.ler();
        if (escolha?.version !== COOKIE_CONSENT_VERSION) return null;
        return [ACEITO, RECUSADO].includes(escolha.status) ? escolha.status : null;
    }

    isPending() {
        return this.status() === null;
    }

    allowsAnalytics() {
        return this.status() !== RECUSADO;
    }

    accept() {
        this.gravar(ACEITO);
        this.analytics?.optIn();
    }

    refuse() {
        this.gravar(RECUSADO);
        this.analytics?.optOut();
    }

    /** Na carga da página: quem já recusou continua fora da análise. */
    applyStoredChoice() {
        if (this.status() === RECUSADO) this.analytics?.optOut();
    }

    /** Cookie primeiro (vale em todos os endereços); senão a cópia local. */
    ler() {
        const doCookie = this.lerCookie();
        if (doCookie) return doCookie;
        const local = this.lerLocal();
        // Resposta de antes do cookie: copia para ele, para valer nos outros endereços também.
        if (local?.version === COOKIE_CONSENT_VERSION) this.gravarCookie(JSON.stringify(local));
        return local;
    }

    lerCookie() {
        try {
            const par = (this.cookies?.cookie ?? "")
                .split("; ")
                .find((item) => item.startsWith(`${COOKIE_CONSENT_KEY}=`));
            if (!par) return null;
            return JSON.parse(decodeURIComponent(par.slice(COOKIE_CONSENT_KEY.length + 1)));
        } catch (err) {
            return null;
        }
    }

    lerLocal() {
        try {
            return JSON.parse(this.storage?.getItem(COOKIE_CONSENT_KEY) || "null");
        } catch (err) {
            return null;
        }
    }

    gravarCookie(valor) {
        if (!this.cookies) return;
        const domain = cookieDomainFor(this.hostname);
        const partes = [
            `${COOKIE_CONSENT_KEY}=${encodeURIComponent(valor)}`,
            "path=/",
            `max-age=${COOKIE_MAX_AGE_SECONDS}`,
            "SameSite=Lax"
        ];
        if (domain) partes.push(`domain=${domain}`, "Secure");
        try {
            this.cookies.cookie = partes.join("; ");
        } catch (err) {
            console.warn("CookieConsent", err);
        }
    }

    gravar(status) {
        const valor = JSON.stringify({
            status,
            version: COOKIE_CONSENT_VERSION,
            at: new Date().toISOString()
        });
        this.gravarCookie(valor);
        try {
            this.storage?.setItem(COOKIE_CONSENT_KEY, valor);
        } catch (err) {
            // Sem armazenamento (navegação anônima restrita): a escolha vale só nesta página.
            console.warn("CookieConsent", err);
        }
    }
}
